import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Merge, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { SearchInput, normText } from "@/components/fly/FilterSelect";
import { CategoryDialog } from "@/components/fly/CategoryDialog";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const perfWord = (n) => (n === 1 ? "występ" : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? "występy" : "występów");

const CategoryRow = ({ c, onEdit, onDelete, onMerge }) => (
  <div className="fly-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`category-row-${slug(c.name)}`}>
    <div className="flex-1 min-w-0">
      <div className="font-semibold">{c.name}</div>
      <div className="text-xs text-slate-400 mt-0.5">{c.is_team ? "Duet / grupa / formacja" : "Solo"} · <span data-testid={`category-signups-${slug(c.name)}`} className={c.max_signups && c.signups >= c.max_signups ? "text-red-300 font-semibold" : ""}>zgłoszeń: {c.signups ?? 0}{c.max_signups ? ` / ${c.max_signups}` : ""}{c.max_signups && c.signups >= c.max_signups ? " – brak miejsc" : ""}</span></div>
      <div className="flex flex-wrap gap-1 mt-2">
        {c.age_groups.map((a) => <span key={a} className="px-2 h-6 inline-flex items-center rounded-full bg-white/5 text-[11px] text-slate-300">{a}{c.signup_ages?.[a] ? ` (${c.signup_ages[a]})` : ""}</span>)}
        {!c.age_groups.length && <span className="text-[11px] text-amber-300">brak grup wiekowych – niewidoczna w zapisach</span>}
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="font-display text-2xl font-bold text-amber-300" data-testid={`category-fee-${slug(c.name)}`}>{c.fee} zł</span>
      {c.fee_per_member && <span className="text-xs text-slate-400">/ os.</span>}
      <button data-testid={`category-merge-${slug(c.name)}`} title="Połącz z inną kategorią" onClick={() => onMerge(c)} className="h-10 px-3 rounded-lg border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 flex items-center gap-1 text-sm"><Merge className="w-4 h-4" />Połącz</button>
      <button data-testid={`category-edit-${slug(c.name)}`} onClick={() => onEdit(c)} className="h-10 w-10 rounded-lg border border-blue-500/30 text-blue-200 hover:bg-blue-500/10 flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
      <button data-testid={`category-delete-${slug(c.name)}`} disabled={c.signups > 0} title={c.signups ? "Można usunąć tylko kategorię bez zgłoszeń" : "Usuń"} onClick={() => onDelete(c)}
        className="h-10 w-10 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center justify-center disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
);

export const CategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [merge, setMerge] = useState(null);
  const load = () => api.get("/admin/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => { const n = normText(q.trim()); return items.filter((c) => !n || normText(c.name).includes(n)); }, [items, q]);

  const remove = async (c) => {
    if (!window.confirm(`Usunąć kategorię ${c.name}?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Usunięto kategorię"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const removeAll = async () => {
    if (!window.confirm("Usunąć WSZYSTKIE kategorie? Kategorie ze zgłoszeniami (oczekującymi lub zatwierdzonymi) zostaną zachowane.")) return;
    try {
      const { data } = await api.delete("/admin/categories");
      toast.success(`Usunięto kategorii: ${data.deleted}${data.kept ? `, zostawiono ${data.kept} ze zgłoszeniami` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-4" data-testid="categories-tab">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1"><SearchInput testid="categories-search" value={q} onChange={setQ} placeholder="Szukaj kategorii…" /></div>
        <button data-testid="category-add-btn" onClick={() => setEdit({})} className="h-12 px-5 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold flex items-center justify-center gap-2"><Plus className="w-4 h-4" />Dodaj kategorię</button>
        <button data-testid="category-delete-all-btn" disabled={!items.length} onClick={removeAll} className="h-12 px-5 rounded-xl border border-red-500/40 text-red-300 hover:bg-red-500/10 font-semibold flex items-center justify-center gap-2 disabled:opacity-30"><Trash2 className="w-4 h-4" />Usuń wszystkie</button>
      </div>
      <p className="text-xs text-slate-400">{items.length} kategorii. Zmiany cen, grup wiekowych i limitów od razu obowiązują w formularzu zapisów. Liczby w nawiasach to zgłoszenia (oczekujące i zatwierdzone).</p>
      {shown.map((c) => <CategoryRow key={c.id} c={c} onEdit={setEdit} onDelete={remove} onMerge={setMerge} />)}
      {!shown.length && <p className="text-slate-400" data-testid="categories-empty">Brak kategorii.</p>}
      <CategoryDialog open={!!edit} cat={edit?.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      <MergeDialog src={merge} items={items} onClose={() => setMerge(null)} onDone={() => { setMerge(null); load(); }} />
    </div>
  );
};

const MergeDialog = ({ src, items, onClose, onDone }) => {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTarget(""); }, [src]);
  const dst = items.find((c) => c.id === target);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/categories/merge", { source_id: src.id, target_id: target });
      toast.success(`Połączono z ${data.target}: przeniesiono zgłoszeń ${data.signups}, występów ${data.performances}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!src} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#1C2541] border-amber-500/40" data-testid="category-merge-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Połącz kategorię</DialogTitle></DialogHeader>
        {src && (
          <div className="grid gap-3 text-sm">
            <p>Dołączasz <b className="text-amber-300">{src.name}</b> (zgłoszeń: {src.signups ?? 0}) do kategorii:</p>
            <select data-testid="category-merge-target" value={target} onChange={(e) => setTarget(e.target.value)} className="h-11 px-3 rounded-xl bg-[#0B132B] border border-blue-500/20">
              <option value="">— wybierz kategorię docelową —</option>
              {items.filter((c) => c.id !== src.id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {dst && dst.is_team !== src.is_team && <p className="text-amber-300">Uwaga: łączysz kategorię solo z kategorią dla duetów / grup.</p>}
            <p className="text-slate-400">Wszystkie zgłoszenia i występy z {src.name} przejdą do kategorii docelowej (grupy wiekowe zostają osobno i zostaną do niej dopisane). Kategoria {src.name} zostanie usunięta z formularza zapisów. Cena kategorii docelowej się nie zmienia.</p>
            <button data-testid="category-merge-confirm" disabled={!target || busy} onClick={run} className="h-11 rounded-xl bg-amber-600 hover:bg-amber-500 font-semibold disabled:opacity-50">{busy ? "Łączę…" : "Połącz"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
